import { useState, useEffect, useCallback, useRef } from "react";
import FilterList from "@mui/icons-material/FilterList";
import {
  Autocomplete,
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  InputAdornment,
  Divider,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Grid,
  Popover,
  Alert,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Search,
  Upload,
  Visibility,
  Close,
  CheckCircle,
  Cancel,
  VideoLibrary,
  PlayArrow,
  Refresh as RefreshIcon,
  Bookmark,
  BookmarkBorder,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission } from "../../utils/adminPermissions";
import { testerHasPermission } from "../../utils/testerPermissions";
import DifficultyChip from "./components/DifficultyChip";
import { QUESTION_TYPES } from "./components/QuestionTypeSelector";
import CategoryTreeSelect from "./components/CategoryTreeSelect";
import HtmlWithLatex from "./components/HtmlWithLatex";
import DiagramHubSpoke from "./components/DiagramHubSpoke";
import DiagramHubSpokeOuter from "./components/DiagramHubSpokeOuter";
import { SubjectChip } from "./components/QuestionBankSubjectChip";
import TopicTreeSelect from "./components/TopicTreeSelect";
import { AggregateChips } from "../Admin/VerificationDashboard";
import { VideoPlayerDialog } from "../../components/VideoExplanationPlayer";

const typeColors = {
  "multiple-choice": "#1976d2",
  "multiple-select": "#7b1fa2",
  "true-false": "#388e3c",
  "fill-in-the-blank": "#f57c00",
  "multi-fill": "#e64a19",
  matching: "#0097a7",
  ordering: "#5d4037",
  essay: "#c2185b",
  "short-answer": "#455a64",
};

const stickyActionsTableCellSx = {
  position: "sticky",
  right: 0,
  bgcolor: "background.paper",
  boxShadow: "-8px 0 12px -8px rgba(0, 0, 0, 0.12)",
  minWidth: 128,
  verticalAlign: "middle",
};

const stickyActionsHeaderSx = {
  ...stickyActionsTableCellSx,
  zIndex: 4,
};

const stickyActionsBodySx = {
  ...stickyActionsTableCellSx,
  zIndex: 2,
};

/** Matches `question_bank_questions.source_type` (see QuestionBankController index filter). */
const SOURCE_TYPE_FILTER_OPTIONS = [
  { value: "", label: "All sources" },
  { value: "manual", label: "Manual" },
  { value: "mock_exam", label: "Mock exam" },
  { value: "past-paper", label: "Past paper" },
  { value: "ai_generated", label: "AI generated" },
  { value: "course_test", label: "Worksheet" },
];

const sourceTypeLabel = (st) => {
  const raw = st || "manual";
  const found = SOURCE_TYPE_FILTER_OPTIONS.find((o) => o.value === raw);
  return found?.label || raw;
};

const sourceTypeChipColor = (st) => {
  switch (st || "manual") {
    case "mock_exam":
      return "info";
    case "past-paper":
      return "warning";
    case "ai_generated":
      return "secondary";
    case "course_test":
      return "success";
    default:
      return "default";
  }
};

const LINKED_CHIP_STYLES = {
  paper: { prefix: "Past paper", tag: "Paper", color: "warning" },
  mock: { prefix: "Mock exam", tag: "Mock", color: "info" },
  sheet: { prefix: "Worksheet", tag: "Sheet", color: "success" },
};

const truncateLinkedName = (name, max = 18) => {
  const s = String(name || "").trim();
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

const linkedTagDisplayName = (item) => {
  const s = String(item.name || "").trim();
  if (item.type === "paper") {
    if (s.length <= 3) return s;
    return `${s.slice(0, 3)}…`;
  }
  return truncateLinkedName(s);
};

const collectLinkedResources = (q) => {
  const items = [];
  (q?.linked_papers || []).forEach((p) => {
    items.push({ key: `paper-${p.id}`, type: "paper", id: p.id, name: p.name });
  });
  const mocks = q?.linked_mock_exams?.length ? q.linked_mock_exams : (q?.linked_exams || []);
  mocks.forEach((m) => {
    items.push({ key: `mock-${m.id}`, type: "mock", id: m.id, name: m.name });
  });
  (q?.linked_worksheets || []).forEach((w) => {
    items.push({ key: `sheet-${w.id}`, type: "sheet", id: w.id, name: w.name });
  });
  return items;
};

const renderLinkedResourceTags = (q) => {
  const items = collectLinkedResources(q);
  if (items.length === 0) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const maxVisible = 2;
  const visible = items.slice(0, maxVisible);
  const rest = items.slice(maxVisible);
  const chipSx = {
    fontSize: "0.62rem",
    height: 20,
    maxWidth: 120,
    "& .MuiChip-label": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      px: 0.75,
      display: "block",
    },
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35, maxWidth: 120 }}>
      {visible.map((item) => {
        const cfg = LINKED_CHIP_STYLES[item.type];
        const fullLabel = `${cfg.prefix}: ${item.name}`;
        return (
          <Tooltip key={item.key} title={`${fullLabel} (#${item.id})`} placement="top-start">
            <Chip
              label={`${cfg.tag}: ${linkedTagDisplayName(item)}`}
              size="small"
              color={cfg.color}
              variant="outlined"
              sx={chipSx}
            />
          </Tooltip>
        );
      })}
      {rest.length > 0 && (
        <Tooltip
          title={rest
            .map((item) => `${LINKED_CHIP_STYLES[item.type].prefix}: ${item.name} (#${item.id})`)
            .join("\n")}
          placement="top-start"
        >
          <Chip
            label={`+${rest.length} more`}
            size="small"
            variant="outlined"
            sx={{ fontSize: "0.6rem", height: 18, maxWidth: 120 }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

const renderLinkedResourceSection = (title, items, type) => {
  if (!items?.length) return null;
  const cfg = LINKED_CHIP_STYLES[type];
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
        {title} ({items.length})
      </Typography>
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
        {items.map((item) => (
          <Chip
            key={`${type}-${item.id}`}
            label={`${cfg.prefix}: ${item.name}`}
            size="small"
            color={cfg.color}
            variant="outlined"
            sx={{ height: "auto", "& .MuiChip-label": { whiteSpace: "normal", py: 0.5 } }}
          />
        ))}
      </Box>
    </Box>
  );
};

const topicChipSx = (topic) => {
  const isSubtopic = topic?.parent_id != null;
  return {
    fontSize: "0.65rem",
    height: 20,
    fontWeight: 600,
    borderColor: isSubtopic ? "#7c3aed" : "#2563eb",
    color: isSubtopic ? "#6d28d9" : "#1d4ed8",
    backgroundColor: isSubtopic ? "rgba(124,58,237,0.08)" : "rgba(37,99,235,0.08)",
  };
};

const renderSubjectCell = (subject) => {
  if (!subject?.name && !subject?.key) {
    return (
      <Typography variant="body2" color="text.secondary" component="span">
        —
      </Typography>
    );
  }
  return <SubjectChip subject={subject} />;
};

const stripHtml = (html) => {
  if (!html) return "";
  const withSpaces = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  try {
    const el = document.createElement("textarea");
    el.innerHTML = withSpaces;
    return el.value.replace(/\s+/g, " ").trim();
  } catch {
    return withSpaces.replace(/&nbsp;/g, " ").replace(/&amp;/g, "&").replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/\s+/g, " ").trim();
  }
};

const BLANK_PLACEHOLDERS = ["□", "[ ]", "[]", "__", "___"];
const detectHubSpokeOuter = (answer) =>
  answer?.diagram_type === "hub-spoke-outer";

const detectHubSpoke = (answer) => {
  if (!answer) return false;
  if (detectHubSpokeOuter(answer)) return true;
  if (answer.diagram_type === "hub-spoke") return true;
  const labels = answer.blank_labels;
  if (!Array.isArray(labels) || labels.length < 2) return false;
  const allHaveBlanks = labels.every((l) =>
    BLANK_PLACEHOLDERS.some((ph) => String(l).includes(ph))
  );
  return allHaveBlanks && /=\s*\S+\s*$/.test(String(labels[0]).trim());
};

const QB_FILTER_KEY = "qb_list_filters";

const QuestionBankList = () => {
  const navigate = useNavigate();
  const canUpdateQuestionBank = adminHasPermission("update-question-bank") || testerHasPermission("seed-question-bank");
  const isMountedRef = useRef(false);

  // Read persisted filters once on mount (useRef initial value is only evaluated once)
  const savedFiltersRef = useRef(() => {
    try { return JSON.parse(sessionStorage.getItem(QB_FILTER_KEY) || "null") ?? {}; }
    catch { return {}; }
  });
  const sf = typeof savedFiltersRef.current === "function"
    ? (savedFiltersRef.current = savedFiltersRef.current())
    : savedFiltersRef.current;

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(sf.page ?? 0);
  // Search + Category, Subject, Type stay visible; the rest sit behind "More filters".
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const [total, setTotal] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(sf.rowsPerPage ?? 10);
  const [jumpPageInput, setJumpPageInput] = useState("");
  // Filters
  const [search, setSearch] = useState(sf.search ?? "");
  const [filterType, setFilterType] = useState(sf.filterType ?? "");
  const [filterCategory, setFilterCategory] = useState(sf.filterCategory ?? "");
  const [filterDifficulty, setFilterDifficulty] = useState(sf.filterDifficulty ?? "");
  const [filterMockExamName, setFilterMockExamName] = useState(sf.filterMockExamName ?? "");
  const [filterHasVideo, setFilterHasVideo] = useState(sf.filterHasVideo ?? "");
  const [filterSample, setFilterSample] = useState(sf.filterSample ?? "");
  const [filterMcqRecommended, setFilterMcqRecommended] = useState(sf.filterMcqRecommended ?? "");
  const [filterTopicIds, setFilterTopicIds] = useState(sf.filterTopicIds ?? []);
  const [topicCatalog, setTopicCatalog] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);
  const [filterYearId, setFilterYearId] = useState(sf.filterYearId ?? "");
  const [bulkAutoTagging, setBulkAutoTagging] = useState(false);
  const [autoTagAllLoading, setAutoTagAllLoading] = useState(false);
  const [untaggedCount, setUntaggedCount] = useState(null);
  const [filterIdFrom, setFilterIdFrom] = useState(sf.filterIdFrom ?? "");
  const [filterIdTo, setFilterIdTo] = useState(sf.filterIdTo ?? "");
  const [filterHasImportIssues, setFilterHasImportIssues] = useState(sf.filterHasImportIssues ?? "");
  const [filterImportIssueType, setFilterImportIssueType] = useState(sf.filterImportIssueType ?? "");
  const [filterSourceType, setFilterSourceType] = useState(sf.filterSourceType ?? "");
  /** Matches `question_bank_questions.mcq_conversion_job_id` (set on questions created by the Convert-to-MCQ feature). */
  const [filterGeneratedViaMcq, setFilterGeneratedViaMcq] = useState(sf.filterGeneratedViaMcq ?? "");
  // Deep link from Recent Imports → "Open Question Bank" for an already-imported paper.
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterImportId, setFilterImportId] = useState(searchParams.get("import_id") ?? "");
  const [filterVerificationCount, setFilterVerificationCount] = useState(sf.filterVerificationCount ?? "");
  const [filterSubjectId, setFilterSubjectId] = useState(sf.filterSubjectId ?? "");
  const [filterSchoolId, setFilterSchoolId] = useState(sf.filterSchoolId ?? "");
  const [filterExamBoardId, setFilterExamBoardId] = useState(sf.filterExamBoardId ?? "");
  /** Same as `filterSubjectId`; some HMR chunks still referenced the old name. */
  const filterExamSubject = filterSubjectId;
  const [examSubjectOptions, setExamSubjectOptions] = useState([]);
  const [schoolOptions, setSchoolOptions] = useState([]);
  const [examBoardOptions, setExamBoardOptions] = useState([]);

  // Mock-exam autocomplete
  const [examOptions, setExamOptions] = useState([]);
  const [examInputValue, setExamInputValue] = useState(sf.examInputValue ?? "");
  const [examLoading, setExamLoading] = useState(false);
  const examSearchTimer = useRef(null);

  // Past-paper autocomplete
  const [filterPaperId, setFilterPaperId] = useState(sf.filterPaperId ?? "");
  const [importOptions, setImportOptions] = useState([]);
  const [importInputValue, setImportInputValue] = useState(sf.importInputValue ?? "");
  const [importLoading, setImportLoading] = useState(false);
  const importSearchTimer = useRef(null);

  // Worksheet autocomplete
  const [filterWorksheetId, setFilterWorksheetId] = useState(sf.filterWorksheetId ?? "");
  const [worksheetOptions, setWorksheetOptions] = useState([]);
  const [worksheetInputValue, setWorksheetInputValue] = useState(sf.worksheetInputValue ?? "");
  const [worksheetLoading, setWorksheetLoading] = useState(false);
  const worksheetSearchTimer = useRef(null);

  // Single question ID filter
  const [filterQuestionId, setFilterQuestionId] = useState(sf.filterQuestionId ?? "");

  // Delete dialog
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deletingQuestion, setDeletingQuestion] = useState(null);

  // View dialog
  const [viewDialog, setViewDialog] = useState(false);
  const [viewQuestion, setViewQuestion] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const [togglingSampleId, setTogglingSampleId] = useState(null);
  const [verificationSummary, setVerificationSummary] = useState({});

  const [filterTesterUserId, setFilterTesterUserId] = useState(sf.filterTesterUserId ?? "");
  // Platform for the "Verified by" filter: web by default (Grokbot QA verifies on web), "" = any.
  const [filterVerifierPlatform, setFilterVerifierPlatform] = useState(sf.filterVerifierPlatform ?? "web");
  const [verifierSummary, setVerifierSummary] = useState(null);
  const [testerOptions, setTesterOptions] = useState([]);

  const [viewVerifications, setViewVerifications] = useState([]);
  const [viewVerificationsLoading, setViewVerificationsLoading] = useState(false);

  const [verifyPopoverAnchor, setVerifyPopoverAnchor] = useState(null);
  const [verifyPopoverQId, setVerifyPopoverQId] = useState(null);
  const [verifyPopoverData, setVerifyPopoverData] = useState([]);
  const [verifyPopoverLoading, setVerifyPopoverLoading] = useState(false);

  const [generatingVideoId, setGeneratingVideoId] = useState(null);
  const [removingVideoId, setRemovingVideoId] = useState(null);
  const IN_PROGRESS_VIDEO_STATUSES = ['pending', 'parsing', 'rendering', 'composing', 'uploading'];

  const [videoPlayerOpen, setVideoPlayerOpen] = useState(false);
  const [videoPlayerUrl, setVideoPlayerUrl] = useState(null);
  const handleOpenVideo = (url) => {
    setVideoPlayerUrl(url);
    setVideoPlayerOpen(true);
  };
  const handleCloseVideo = () => {
    setVideoPlayerOpen(false);
    setVideoPlayerUrl(null);
  };

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        order_by: 'id',
        order_dir: 'desc',
      };
      if (search) params.search = search;
      if (filterType) params.type = filterType;
      if (filterCategory) params.category_id = filterCategory;
      if (filterDifficulty) params.difficulty = filterDifficulty;
      if (filterMockExamName) params.mock_exam_name = filterMockExamName;
      if (filterHasVideo === "yes" || filterHasVideo === "no") params.has_video = filterHasVideo;
      if (filterSample === "yes" || filterSample === "no") params.is_sample = filterSample;
      if (filterMcqRecommended === "yes" || filterMcqRecommended === "no") params.mcq_recommended = filterMcqRecommended;
      if (filterTopicIds.length > 0) params.topic_ids = filterTopicIds;
      if (filterQuestionId) params.id = filterQuestionId;
      if (filterIdFrom) params.id_from = filterIdFrom;
      if (filterIdTo) params.id_to = filterIdTo;
      if (filterHasImportIssues === "yes" || filterHasImportIssues === "no") params.has_import_issues = filterHasImportIssues;
      if (filterImportIssueType) params.import_issue_type = filterImportIssueType;
      if (filterSourceType) params.source_type = filterSourceType;
      if (filterGeneratedViaMcq === "yes" || filterGeneratedViaMcq === "no") params.generated_via_mcq = filterGeneratedViaMcq;
      if (filterVerificationCount) params.min_verifiers = filterVerificationCount;
      if (filterSubjectId) params.subject_id = filterSubjectId;
      if (filterSchoolId) params.school_id = filterSchoolId;
      if (filterExamBoardId) params.exam_board_id = filterExamBoardId;
      if (filterPaperId) params.paper_id = filterPaperId;
      if (filterImportId) params.import_id = filterImportId;
      if (filterWorksheetId) params.course_test_id = filterWorksheetId;
      if (filterTesterUserId) {
        params.verifier_id = filterTesterUserId;
        if (filterVerifierPlatform) params.verifier_platform = filterVerifierPlatform;
      }

      const res = await api.get("/admin/question-bank", { params });
      const data = res.data?.data;
      const qs = data?.data || [];
      setQuestions(qs);
      setTotal(data?.total || 0);
      setVerifierSummary(data?.verifier_summary ?? null);
      if (qs.length > 0) {
        api.post("/admin/content-verifications/batch-summary", {
          items: qs.map((q) => ({ type: "question", id: q.id })),
        }).then((r) => {
          if (r.data?.success) setVerificationSummary(r.data.data ?? {});
        }).catch(() => {});
      }
    } catch (err) {
      toast.error("Failed to load questions");
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, filterType, filterCategory, filterDifficulty, filterMockExamName, filterHasVideo, filterSample, filterMcqRecommended, filterTopicIds, filterQuestionId, filterIdFrom, filterIdTo, filterHasImportIssues, filterImportIssueType, filterSourceType, filterGeneratedViaMcq, filterVerificationCount, filterSubjectId, filterSchoolId, filterExamBoardId, filterPaperId, filterImportId, filterWorksheetId, filterTesterUserId, filterVerifierPlatform]);

  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      fetchQuestions();
      return;
    }
    const timer = setTimeout(fetchQuestions, 300);
    return () => clearTimeout(timer);
  }, [fetchQuestions]);

  useEffect(() => {
    if (total <= 0) {
      setJumpPageInput("");
      return;
    }
    setJumpPageInput(String(page + 1));
  }, [page, total]);

  useEffect(() => {
    const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
    if (page > totalPages - 1) {
      setPage(totalPages - 1);
    }
  }, [page, total, rowsPerPage]);

  useEffect(() => {
    const hasInProgress = questions.some(
      (q) => q.video_generation_status && IN_PROGRESS_VIDEO_STATUSES.includes(q.video_generation_status)
    );
    if (!hasInProgress) return;
    const pollTimer = setInterval(fetchQuestions, 5000);
    return () => clearInterval(pollTimer);
  }, [questions, fetchQuestions]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch mock exams for the autocomplete — debounced as user types
  useEffect(() => {
    clearTimeout(examSearchTimer.current);
    examSearchTimer.current = setTimeout(async () => {
      setExamLoading(true);
      try {
        const params = { per_page: 50, order_by: "name", order_dir: "asc" };
        if (examInputValue) params.search = examInputValue;
        if (filterSubjectId) params.subject_id = filterSubjectId;
        const res = await api.get("/admin/mock-exam", { params });
        const items = res.data?.data?.data ?? res.data?.data ?? [];
        setExamOptions(items.map((e) => ({ id: e.id, name: e.name })));
      } catch {
        // silently ignore — dropdown just stays empty
      } finally {
        setExamLoading(false);
      }
    }, 300);
    return () => clearTimeout(examSearchTimer.current);
  }, [examInputValue, filterSubjectId]);

  // Fetch past papers for the autocomplete — debounced as user types
  useEffect(() => {
    clearTimeout(importSearchTimer.current);
    importSearchTimer.current = setTimeout(async () => {
      setImportLoading(true);
      try {
        const params = { per_page: 50 };
        if (importInputValue) params.search = importInputValue;
        if (filterSubjectId) params.subject_id = filterSubjectId;
        const res = await api.get("/admin/paper", { params });
        const items = res.data?.data?.data;
        setImportOptions(
          Array.isArray(items) ? items.map((p) => ({ ...p, _type: "paper", _label: p.name })) : []
        );
      } catch {
        // silently ignore
      } finally {
        setImportLoading(false);
      }
    }, 300);
    return () => clearTimeout(importSearchTimer.current);
  }, [importInputValue, filterSubjectId]);

  // Fetch worksheets for the autocomplete — debounced as user types
  useEffect(() => {
    clearTimeout(worksheetSearchTimer.current);
    worksheetSearchTimer.current = setTimeout(async () => {
      setWorksheetLoading(true);
      try {
        const params = { per_page: 50 };
        if (worksheetInputValue) params.search = worksheetInputValue;
        if (filterSubjectId) params.subject_id = filterSubjectId;
        const res = await api.get("/admin/question-bank/worksheets", { params });
        const items = res.data?.data ?? [];
        setWorksheetOptions(Array.isArray(items) ? items.map((t) => ({ id: t.id, name: t.name })) : []);
      } catch {
        // silently ignore
      } finally {
        setWorksheetLoading(false);
      }
    }, 300);
    return () => clearTimeout(worksheetSearchTimer.current);
  }, [worksheetInputValue, filterSubjectId]);

  useEffect(() => {
    setFilterMockExamName(""); setExamInputValue("");
    setFilterPaperId(""); setImportInputValue("");
    setFilterWorksheetId(""); setWorksheetInputValue("");
    if (!filterSubjectId) {
      setTopicCatalog([]);
      setFilterTopicIds([]);
      return;
    }
    let cancelled = false;
    const params = { subject_id: filterSubjectId, include_counts: 1 };
    if (filterYearId) params.school_year_id = filterYearId;
    api.get("/admin/question-bank/topics-by-subject", { params })
      .then((res) => { if (!cancelled) setTopicCatalog(res.data?.data || []); })
      .catch(() => { if (!cancelled) setTopicCatalog([]); });
    return () => { cancelled = true; };
  }, [filterSubjectId, filterYearId]);

  useEffect(() => {
    api.get("/admin/question-bank/exam-subjects")
      .then((res) => setExamSubjectOptions(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/master-form/school-years")
      .then((res) => {
        const rows = res.data?.data || [];
        setSchoolYears(rows);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/admin/question-bank/schools")
      .then((res) => setSchoolOptions(res.data?.data || []))
      .catch(() => {});
    api.get("/admin/question-bank/exam-boards")
      .then((res) => setExamBoardOptions(res.data?.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/admin/content-verifications/verifiers")
      .then((res) => {
        if (!cancelled) {
          const list = res.data?.data;
          setTesterOptions(Array.isArray(list) ? list : []);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[QB] verifiers fetch failed:", err?.response?.status, err?.message);
        }
      });
    return () => { cancelled = true; };
  }, []);

  const handleDelete = async () => {
    if (!deletingQuestion) return;
    try {
      await api.delete(`/admin/question-bank/${deletingQuestion.id}`);
      toast.success("Question deleted");
      setDeleteDialog(false);
      setDeletingQuestion(null);
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete question");
    }
  };

  const handleView = async (q) => {
    setViewDialog(true);
    setViewQuestion(null);
    setViewLoading(true);
    setViewVerifications([]);
    setViewVerificationsLoading(true);
    try {
      const [qRes, vRes] = await Promise.allSettled([
        api.get(`/admin/question-bank/${q.id}`),
        api.get('/admin/content-verifications', { params: { type: 'question', verifiable_id: q.id, per_page: 50 } }),
      ]);
      if (qRes.status === 'fulfilled') {
        setViewQuestion(qRes.value.data?.data || null);
      } else {
        toast.error("Failed to load question details");
        setViewDialog(false);
      }
      if (vRes.status === 'fulfilled') {
        const d = vRes.value.data?.data;
        setViewVerifications(d?.verifications ?? d?.data ?? []);
      }
    } finally {
      setViewLoading(false);
      setViewVerificationsLoading(false);
    }
  };

  const handleGenerateVideo = async (q) => {
    setGeneratingVideoId(q.id);
    try {
      await api.post(`/admin/question-bank/${q.id}/generate-video`, { regenerate: true });
      toast.success("Video generation started");
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to start video generation");
    } finally {
      setGeneratingVideoId(null);
    }
  };

  const handleRemoveVideo = async (q) => {
    setRemovingVideoId(q.id);
    try {
      await api.post(`/admin/question-bank/${q.id}/remove-video`, {});
      toast.success("Video removed");
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to remove video");
    } finally {
      setRemovingVideoId(null);
    }
  };

  const handleToggleSample = async (q) => {
    if (!canUpdateQuestionBank) return;
    setTogglingSampleId(q.id);
    try {
      await api.patch(`/admin/question-bank/${q.id}`, {
        is_sample_question: !q.is_sample_question,
      });
      toast.success(q.is_sample_question ? "Sample flag removed" : "Marked as sample question");
      fetchQuestions();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update sample flag");
    } finally {
      setTogglingSampleId(null);
    }
  };

  const handleVerifyChipClick = async (e, qId) => {
    setVerifyPopoverAnchor(e.currentTarget);
    setVerifyPopoverQId(qId);
    setVerifyPopoverLoading(true);
    setVerifyPopoverData([]);
    try {
      const res = await api.get('/admin/content-verifications', {
        params: { type: 'question', verifiable_id: qId, per_page: 50 },
      });
      const d = res.data?.data;
      setVerifyPopoverData(d?.verifications ?? d?.data ?? []);
    } catch {
      setVerifyPopoverData([]);
    } finally {
      setVerifyPopoverLoading(false);
    }
  };

  const activeFilterCount = [
    search, filterType, filterCategory, filterDifficulty,
    filterMockExamName, filterHasVideo, filterSample, filterMcqRecommended,
    filterQuestionId, filterIdFrom, filterIdTo,
    filterHasImportIssues, filterImportIssueType,
    filterSourceType, filterGeneratedViaMcq, filterVerificationCount, filterSubjectId, filterSchoolId,
    filterExamBoardId, filterPaperId, filterWorksheetId, filterYearId, filterTesterUserId,
  ].filter(Boolean).length + (filterTopicIds.length > 0 ? 1 : 0);

  const handleClearFilters = () => {
    setSearch(""); setFilterType(""); setFilterCategory("");
    setFilterDifficulty(""); setFilterMockExamName(""); setFilterHasVideo(""); setFilterSample(""); setFilterMcqRecommended("");
    setFilterTopicIds([]);
    setFilterQuestionId(""); setFilterIdFrom(""); setFilterIdTo("");
    setFilterHasImportIssues(""); setFilterImportIssueType("");
    setFilterSourceType(""); setFilterGeneratedViaMcq(""); setFilterVerificationCount(""); setFilterSubjectId(""); setFilterSchoolId("");
    setFilterExamBoardId(""); setFilterPaperId(""); setFilterWorksheetId(""); setFilterYearId("");
    setFilterTesterUserId(""); setFilterVerifierPlatform("web");
    setExamInputValue(""); setImportInputValue(""); setWorksheetInputValue("");
    setPage(0);
    try { sessionStorage.removeItem(QB_FILTER_KEY); } catch {}
  };

  // Persist all filter + pagination state so navigating to edit and back restores them
  useEffect(() => {
    try {
      sessionStorage.setItem(QB_FILTER_KEY, JSON.stringify({
        search, filterType, filterCategory, filterDifficulty,
        filterMockExamName, filterHasVideo, filterSample, filterMcqRecommended, filterTopicIds,
        filterYearId, filterQuestionId, filterIdFrom, filterIdTo,
        filterHasImportIssues, filterImportIssueType, filterSourceType, filterGeneratedViaMcq,
        filterVerificationCount, filterSubjectId, filterSchoolId, filterExamBoardId,
        filterPaperId, filterWorksheetId, importInputValue, examInputValue, worksheetInputValue,
        filterTesterUserId, filterVerifierPlatform,
        page, rowsPerPage,
      }));
    } catch {}
  }, [
    search, filterType, filterCategory, filterDifficulty,
    filterMockExamName, filterHasVideo, filterSample, filterMcqRecommended, filterTopicIds,
    filterYearId, filterQuestionId, filterIdFrom, filterIdTo,
    filterHasImportIssues, filterImportIssueType, filterSourceType, filterGeneratedViaMcq,
    filterVerificationCount, filterSubjectId, filterSchoolId, filterExamBoardId,
    filterPaperId, filterWorksheetId, importInputValue, examInputValue, worksheetInputValue,
    filterTesterUserId, filterVerifierPlatform,
    page, rowsPerPage,
  ]);

  const handleBulkAutoTag = async () => {
    const ids = questions.map((q) => q.id);
    if (ids.length === 0) return;
    setBulkAutoTagging(true);
    try {
      await api.post("/admin/question-bank/auto-tag-bulk", { ids, skip_if_already_tagged: true });
      toast.success(`Auto-tag job queued for ${ids.length} questions`);
      setTimeout(fetchQuestions, 2000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to dispatch auto-tag");
    } finally {
      setBulkAutoTagging(false);
    }
  };

  // Must carry the same scope the dispatch below sends, or the badge counts a set the button
  // does not queue.
  const fetchUntaggedCount = useCallback(async () => {
    try {
      const params = { per_page: 1, no_topics: 1 };
      if (filterSubjectId) params.subject_id = filterSubjectId;
      if (filterSourceType) params.source_type = filterSourceType;
      const res = await api.get("/admin/question-bank", { params });
      setUntaggedCount(res.data?.data?.total ?? null);
    } catch {
      setUntaggedCount(null);
    }
  }, [filterSubjectId, filterSourceType]);

  useEffect(() => {
    const t = setTimeout(fetchUntaggedCount, 1000);
    return () => clearTimeout(t);
  }, [fetchUntaggedCount]);

  const handleAutoTagAllUntagged = async () => {
    setAutoTagAllLoading(true);
    try {
      const params = {};
      if (filterSubjectId) params.subject_id = filterSubjectId;
      if (filterSourceType) params.source_type = filterSourceType;
      const res = await api.post("/admin/question-bank/auto-tag-all-untagged", params);
      const queued = res.data?.data?.queued ?? 0;
      toast.success(`Auto-tag jobs dispatched for ${queued} untagged questions. Topics will appear as the queue processes.`);
      setUntaggedCount(0);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to dispatch auto-tag");
    } finally {
      setAutoTagAllLoading(false);
    }
  };

  const getTypeLabel = (type) =>
    QUESTION_TYPES.find((t) => t.value === type)?.label || type;

  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleJumpToPage = () => {
    if (!total) return;
    const numericPage = Number.parseInt(jumpPageInput, 10);
    if (Number.isNaN(numericPage)) {
      setJumpPageInput(String(page + 1));
      return;
    }
    const clampedPage = Math.min(Math.max(numericPage, 1), totalPages);
    setPage(clampedPage - 1);
    setJumpPageInput(String(clampedPage));
  };

  // ── View Dialog ──────────────────────────────────────────────────────────
  const renderViewDialog = () => {
    const q = viewQuestion;
    return (
      <Dialog
        open={viewDialog}
        onClose={() => { setViewDialog(false); setViewVerifications([]); }}
        maxWidth="md"
        fullWidth
        keepMounted={false}
        aria-modal="true"
        PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
      >
        {/* Title bar */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #3f2b96 0%, #a71d31 100%)",
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 2,
            px: 3,
          }}
        >
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Question Details
            </Typography>
            {q && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                ID: #{q.id}
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => setViewDialog(false)}
            sx={{ color: "white", "&:hover": { bgcolor: "rgba(255,255,255,0.15)" } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          {viewLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : q ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>

              {/* Meta row */}
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
                {/* ID badge */}
                <Chip
                  label={`ID: ${q.id}`}
                  size="small"
                  sx={{
                    fontWeight: 700,
                    bgcolor: "#f3e5f5",
                    color: "#7b1fa2",
                    fontSize: "0.8rem",
                    px: 0.5,
                  }}
                />
                {/* Type */}
                <Chip
                  label={getTypeLabel(q.type)}
                  size="small"
                  sx={{
                    backgroundColor: (typeColors[q.type] || "#555") + "20",
                    color: typeColors[q.type] || "#555",
                    fontWeight: 600,
                    fontSize: "0.75rem",
                  }}
                />
                {/* Difficulty */}
                <DifficultyChip difficulty={q.difficulty} />
                {/* Marks */}
                <Chip
                  label={`${q.default_marks} mark${q.default_marks !== 1 ? "s" : ""}`}
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: "0.75rem" }}
                />
                {/* Duration */}
                {q.default_duration_sec != null && (
                  <Chip
                    label={`${q.default_duration_sec}s`}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
                {/* Category */}
                {q.category_name && (
                  <Chip
                    label={q.category_name}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
                {q.subject && renderSubjectCell(q.subject)}
                {/* Source (import / manual / AI / course test) */}
                <Chip
                  label={sourceTypeLabel(q.source_type)}
                  size="small"
                  color={sourceTypeChipColor(q.source_type)}
                  variant="outlined"
                  sx={{ fontSize: "0.75rem" }}
                />
                {q.mcq_conversion_job_id && (
                  <Chip
                    label="MCQ generated"
                    size="small"
                    color="secondary"
                    variant="outlined"
                    sx={{ fontSize: "0.75rem" }}
                  />
                )}
              </Box>

              <Divider />

              {/* Hub-spoke diagram — rendered natively when detected */}
              {detectHubSpoke(q.answer) ? (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                    Diagram
                  </Typography>
                  {detectHubSpokeOuter(q.answer) ? (
                    <DiagramHubSpokeOuter
                      blankLabels={q.answer.blank_labels || []}
                      hubValue={q.answer.hub_value}
                      showAnswers
                      correctBlanks={q.answer.correct_blanks || []}
                    />
                  ) : (
                    <DiagramHubSpoke
                      blankLabels={q.answer.blank_labels || []}
                      hubValue={q.answer.hub_value}
                      showAnswers
                      correctBlanks={q.answer.correct_blanks || []}
                    />
                  )}
                </Box>
              ) : (
                <>
                  {/* Fallback: static image */}
                  {q.image_url && (
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                        Image
                      </Typography>
                      <Box
                        component="img"
                        src={q.image_url}
                        alt="Question"
                        sx={{
                          maxWidth: "100%",
                          maxHeight: 280,
                          borderRadius: 2,
                          border: "1px solid #e0e0e0",
                          display: "block",
                        }}
                      />
                    </Box>
                  )}

                  {/* Diagram description for non-hub-spoke questions */}
                  {q.image_description && (
                    <Box
                      sx={{
                        mb: 2,
                        border: "1px dashed",
                        borderColor: q.image_url ? "warning.300" : "primary.300",
                        borderRadius: 2,
                        overflow: "hidden",
                        backgroundColor: q.image_url ? "warning.50" : "transparent",
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 0.75,
                          bgcolor: q.image_url ? "warning.100" : "primary.50",
                          borderBottom: "1px dashed",
                          borderColor: q.image_url ? "warning.200" : "primary.200",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <Typography variant="caption" sx={{ fontWeight: 700, color: q.image_url ? "warning.800" : "primary.700", textTransform: "uppercase", letterSpacing: 0.5 }}>
                          Diagram
                        </Typography>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {q.image_url ? "(description for reference)" : "(image not extracted — use Generate Diagram or Add Image to attach one)"}
                        </Typography>
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", lineHeight: 1.7 }}>
                          {q.image_description}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}

              {/* Question text */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Question Text
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2, bgcolor: "#fafafa", lineHeight: 1.7 }}
                >
                  <HtmlWithLatex html={q.question_text || ""} />
                </Paper>
              </Box>

              {/* Sub-questions (grouped type) */}
              {q.type === "grouped" && Array.isArray(q.sub_questions) && q.sub_questions.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: "text.secondary" }}>
                    Sub-questions ({q.sub_questions.length})
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {q.sub_questions.map((sq, idx) => (
                      <Paper
                        key={sq.id ?? idx}
                        variant="outlined"
                        sx={{ borderRadius: 2, overflow: "hidden", borderColor: "#e0e0e0" }}
                      >
                        {/* Sub-question header */}
                        <Box
                          sx={{
                            px: 2,
                            py: 1,
                            bgcolor: "#f5f5f5",
                            borderBottom: "1px solid #e0e0e0",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            flexWrap: "wrap",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              color: "#7b1fa2",
                              bgcolor: "#f3e5f5",
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.25,
                              fontSize: "0.75rem",
                              fontFamily: "monospace",
                            }}
                          >
                            #{idx + 1}
                          </Typography>
                          <Chip
                            label={getTypeLabel(sq.type)}
                            size="small"
                            sx={{
                              backgroundColor: (typeColors[sq.type] || "#555") + "20",
                              color: typeColors[sq.type] || "#555",
                              fontWeight: 600,
                              fontSize: "0.68rem",
                              height: 20,
                            }}
                          />
                          {sq.default_marks != null && (
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              {sq.default_marks} mark{sq.default_marks !== 1 ? "s" : ""}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                          {/* Section instructions */}
                          {sq.section_instructions && (
                            <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                              {sq.section_instructions}
                            </Typography>
                          )}

                          {/* Image */}
                          {sq.image_url && (
                            <Box
                              component="img"
                              src={sq.image_url}
                              alt={`Sub-question ${idx + 1}`}
                              sx={{
                                maxWidth: "100%",
                                maxHeight: 220,
                                borderRadius: 1,
                                border: "1px solid #e0e0e0",
                                display: "block",
                              }}
                            />
                          )}

                          {/* Question text */}
                          {sq.question_text && (
                            <HtmlWithLatex html={sq.question_text} />
                          )}

                          {/* Options */}
                          {["multiple-choice", "multiple-select", "true-false"].includes(sq.type) &&
                            sq.options?.length > 0 && (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                {sq.options.map((opt, oi) => (
                                  <Box
                                    key={opt.id ?? oi}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      p: 1,
                                      borderRadius: 1.5,
                                      border: "1px solid",
                                      borderColor: opt.is_correct ? "success.main" : "#e0e0e0",
                                      bgcolor: opt.is_correct ? "rgba(46,125,50,0.05)" : "transparent",
                                    }}
                                  >
                                    {opt.is_correct ? (
                                      <CheckCircle sx={{ color: "success.main", fontSize: 16, flexShrink: 0 }} />
                                    ) : (
                                      <Cancel sx={{ color: "#bdbdbd", fontSize: 16, flexShrink: 0 }} />
                                    )}
                                    {opt.image_url ? (
                                      <Box component="img" src={opt.image_url} sx={{ maxHeight: 60, maxWidth: 80, borderRadius: 1 }} />
                                    ) : (
                                      <Typography variant="body2" sx={{ fontWeight: opt.is_correct ? 600 : 400 }}>
                                        {opt.option_text || ""}
                                      </Typography>
                                    )}
                                  </Box>
                                ))}
                              </Box>
                            )}

                          {/* Short / fill-in answer */}
                          {(sq.type === "short-answer" || sq.type === "fill-in-the-blank" || sq.type === "multi-fill" || sq.type === "essay") &&
                            sq.answer && (
                              <Box
                                sx={{
                                  p: 1,
                                  borderRadius: 1.5,
                                  border: "1px solid",
                                  borderColor: "success.light",
                                  bgcolor: "rgba(46,125,50,0.04)",
                                }}
                              >
                                <Typography variant="caption" sx={{ fontWeight: 700, color: "success.dark", display: "block", mb: 0.25 }}>
                                  Answer
                                </Typography>
                                {sq.answer.correct_answer_text && (
                                  <HtmlWithLatex html={sq.answer.correct_answer_text} />
                                )}
                                {sq.answer.correct_blanks?.length > 0 && (
                                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                                    {sq.answer.correct_blanks.map((b, bi) => (
                                      <Chip key={bi} label={Array.isArray(b) ? b[0] : b} size="small" color="success" variant="outlined" />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            )}
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Options (MCQ types) — parent question only */}
              {["multiple-choice", "multiple-select", "true-false"].includes(q.type) &&
                q.options?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                      Options
                    </Typography>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      {q.options.map((opt, idx) => (
                        <Box
                          key={opt.id ?? idx}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 1.5,
                            borderRadius: 2,
                            border: "1px solid",
                            borderColor: opt.is_correct ? "success.main" : "#e0e0e0",
                            bgcolor: opt.is_correct ? "rgba(46,125,50,0.06)" : "transparent",
                          }}
                        >
                          {opt.is_correct ? (
                            <CheckCircle sx={{ color: "success.main", fontSize: 20, flexShrink: 0 }} />
                          ) : (
                            <Cancel sx={{ color: "#bdbdbd", fontSize: 20, flexShrink: 0 }} />
                          )}
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: opt.is_correct ? 600 : 400 }}
                          >
                            {opt.option_text || opt.text || ""}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

              {/* Matching pairs */}
              {q.type === "matching" && q.options?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                    Matching Pairs
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {q.options.map((opt, idx) => (
                      <Box
                        key={opt.id ?? idx}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 1.5,
                          borderRadius: 2,
                          border: "1px solid #e0e0e0",
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {opt.option_text || opt.text}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "text.secondary", mx: 1 }}>→</Typography>
                        <Typography variant="body2" sx={{ flex: 1, color: "primary.main", fontWeight: 500 }}>
                          {opt.match_text || "—"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Ordering */}
              {q.type === "ordering" && q.options?.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                    Correct Order
                  </Typography>
                  <List dense disablePadding>
                    {q.options.map((opt, idx) => (
                      <ListItem
                        key={opt.id ?? idx}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          border: "1px solid #e0e0e0",
                          bgcolor: "#fafafa",
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700, mr: 1.5, color: "text.secondary", minWidth: 24 }}>
                          {idx + 1}.
                        </Typography>
                        <ListItemText primary={opt.option_text || opt.text} />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Answer data */}
              {q.answer && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                    Answer / Model Answer
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "rgba(46,125,50,0.05)", borderColor: "success.light" }}>
                    {/* Correct answer text (essay / short-answer) */}
                    {q.answer.correct_answer_text && (
                      <Box sx={{ "& p": { m: 0 } }}>
                        <HtmlWithLatex html={q.answer.correct_answer_text} />
                      </Box>
                    )}
                    {/* Correct blanks (fill-in-the-blank) */}
                    {q.answer.correct_blanks?.length > 0 && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {q.answer.correct_blanks.map((b, i) => (
                          <Chip
                            key={i}
                            label={Array.isArray(b) ? b[0] : b}
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                    {/* Correct options (multiple-choice / multiple-select) */}
                    {q.answer.correct_options?.length > 0 && (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {q.answer.correct_options.map((opt, i) => {
                          const optIdx = parseInt(opt, 10);
                          const resolvedText = (!isNaN(optIdx) && q.options?.[optIdx])
                            ? (q.options[optIdx].option_text || q.options[optIdx].text || String(opt))
                            : String(opt);
                          return <Chip key={i} label={resolvedText} size="small" color="success" variant="outlined" />;
                        })}
                      </Box>
                    )}
                    {/* Correct matches */}
                    {q.answer.correct_matches?.length > 0 && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {q.answer.correct_matches.map((m, i) => (
                          <Typography key={i} variant="body2">
                            {m.left} → {m.right}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {/* Correct ordering */}
                    {q.answer.correct_ordering_items?.length > 0 && (
                      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {q.answer.correct_ordering_items.map((item, i) => (
                          <Chip key={i} label={`${i + 1}. ${item}`} size="small" color="success" variant="outlined" />
                        ))}
                      </Box>
                    )}
                  </Paper>
                </Box>
              )}

              {/* Topics chips */}
              {Array.isArray(q.topics) && q.topics.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                    Topics
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
                    {q.topics.map((t) => (
                      <Chip
                        key={t.id}
                        label={t.name}
                        size="small"
                        variant="outlined"
                        sx={{
                          ...topicChipSx(t),
                          height: 24,
                          fontSize: "0.72rem",
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {renderLinkedResourceSection(
                "Linked Papers",
                q.linked_papers,
                "paper"
              )}
              {renderLinkedResourceSection(
                "Linked Mock Exams",
                q.linked_mock_exams?.length ? q.linked_mock_exams : q.linked_exams,
                "mock"
              )}
              {renderLinkedResourceSection(
                "Linked Worksheets",
                q.linked_worksheets,
                "sheet"
              )}

              {/* Verified by */}
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: "text.secondary" }}>
                  Verified By
                </Typography>
                {viewVerificationsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={14} />
                    <Typography variant="caption" color="text.secondary">Loading…</Typography>
                  </Box>
                ) : viewVerifications.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                    Not yet verified by any tester.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                    {viewVerifications.map((v) => {
                      const cfg = v.status === "approved"
                        ? { label: "Approved", color: "success" }
                        : v.status === "rejected"
                          ? { label: "Rejected", color: "error" }
                          : { label: v.status, color: "default" };
                      return (
                        <Box key={v.id} sx={{ display: "flex", alignItems: "center", gap: 1, p: 1, borderRadius: 1.5, border: "1px solid #e0e0e0", bgcolor: "#fafafa" }}>
                          <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                            {v.user?.name ?? `User #${v.user_id}`}
                          </Typography>
                          <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />
                          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                            {v.verified_at ? new Date(v.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
              No data available.
            </Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          {q && (
            <Button
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => {
                setViewDialog(false);
                navigate(`/admin/question-bank/${q.id}/edit`);
              }}
              sx={{ textTransform: "none" }}
            >
              Edit Question
            </Button>
          )}
          <Button onClick={() => { setViewDialog(false); setViewVerifications([]); }} sx={{ textTransform: "none" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Question Bank
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create, edit, and manage all questions for your exams.
          </Typography>
          {filterImportId && (
            <Chip
              size="small"
              color="primary"
              variant="outlined"
              sx={{ mt: 1 }}
              label={`Showing questions from import #${filterImportId}`}
              onDelete={() => {
                setFilterImportId("");
                searchParams.delete("import_id");
                setSearchParams(searchParams, { replace: true });
              }}
            />
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={handleBulkAutoTag}
            disabled={loading || bulkAutoTagging || questions.length === 0}
            startIcon={bulkAutoTagging ? <CircularProgress size={14} /> : null}
          >
            {bulkAutoTagging ? "Queuing…" : "AI auto-tag this page"}
          </Button>
          {untaggedCount > 0 && (
            <Button
              variant="contained"
              color="warning"
              onClick={handleAutoTagAllUntagged}
              disabled={autoTagAllLoading}
              startIcon={autoTagAllLoading ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : null}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              {autoTagAllLoading ? "Dispatching…" : `AI auto-tag all untagged (${untaggedCount})`}
            </Button>
          )}

          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => navigate("/admin/question-bank/import")}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/admin/question-bank/create")}
            sx={{
              background: "linear-gradient(to right, #3f2b96, #a71d31)",
              "&:hover": { background: "linear-gradient(to right, #3f2b96, #8b1a28)" },
            }}
          >
            Add Question
          </Button>
        </Box>
      </Box>

      {/* Filters - Single row using Grid */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
            Filters
            {activeFilterCount > 0 && (
              <Chip
                label={`${activeFilterCount} active`}
                size="small"
                color="primary"
                sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
              />
            )}
          </Typography>
          {activeFilterCount > 0 && (
            <Button size="small" onClick={handleClearFilters} sx={{ textTransform: "none", color: "text.secondary" }}>
              Clear all
            </Button>
          )}
        </Box>
        <Divider sx={{ mb: 2 }} />
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search questions..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <CategoryTreeSelect
              fullWidth
              value={filterCategory}
              onChange={(val) => { setFilterCategory(val); setPage(0); }}
              label="All Categories"
              noneLabel="All Categories"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Subject</InputLabel>
              <Select
                value={filterExamSubject}
                label="Subject"
                onChange={(e) => { setFilterSubjectId(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All subjects</MenuItem>
                {examSubjectOptions.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>All Types</InputLabel>
              <Select
                value={filterType}
                label="All Types"
                onChange={(e) => { setFilterType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                {QUESTION_TYPES.map((t) => (
                  <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <Autocomplete
              options={schoolOptions}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              value={schoolOptions.find((o) => String(o.id) === String(filterSchoolId)) ?? null}
              onChange={(_, selected) => {
                setFilterSchoolId(selected ? String(selected.id) : "");
                setPage(0);
              }}
              size="small"
              clearOnEscape
              renderInput={(params) => (
                <TextField {...params} label="School" placeholder="Search school…" />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Exam Board</InputLabel>
              <Select
                value={filterExamBoardId}
                label="Exam Board"
                onChange={(e) => { setFilterExamBoardId(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All exam boards</MenuItem>
                {examBoardOptions.map((b) => (
                  <MenuItem key={b.id} value={String(b.id)}>
                    {b.code ? `${b.name} (${b.code})` : b.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Difficulty</InputLabel>
              <Select
                value={filterDifficulty}
                label="Difficulty"
                onChange={(e) => { setFilterDifficulty(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="Easy">Easy</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Hard">Hard</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={filterSourceType}
                label="Source"
                onChange={(e) => { setFilterSourceType(e.target.value); setPage(0); }}
              >
                {SOURCE_TYPE_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value || "all"} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>MCQ generated</InputLabel>
              <Select
                value={filterGeneratedViaMcq}
                label="MCQ generated"
                onChange={(e) => { setFilterGeneratedViaMcq(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Generated via MCQ conversion</MenuItem>
                <MenuItem value="no">Not MCQ-generated</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Verifier count</InputLabel>
              <Select
                value={filterVerificationCount}
                label="Verifier count"
                onChange={(e) => { setFilterVerificationCount(e.target.value); setPage(0); }}
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="0">Unverified</MenuItem>
                <MenuItem value="1">1+ tester verified</MenuItem>
                <MenuItem value="2">2+ testers verified</MenuItem>
                <MenuItem value="3">3+ testers verified</MenuItem>
                <MenuItem value="4">4+ testers verified</MenuItem>
                <MenuItem value="5">5+ testers verified</MenuItem>
                <MenuItem value="6">6+ testers verified</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <Autocomplete
              options={testerOptions}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
              isOptionEqualToValue={(o, v) => String(o.id) === String(v.id)}
              value={testerOptions.find((o) => String(o.id) === String(filterTesterUserId)) ?? null}
              onChange={(_, selected) => {
                setFilterTesterUserId(selected ? String(selected.id) : "");
                setPage(0);
              }}
              size="small"
              clearOnEscape
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <span style={{ flex: 1 }}>{option.name ?? option}</span>
                  {option.total_verifications > 0 && (
                    <Chip
                      label={option.total_verifications}
                      size="small"
                      variant="outlined"
                      color="primary"
                      sx={{ ml: 1, fontSize: "0.68rem", height: 20 }}
                    />
                  )}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Verified by" placeholder="Search verifier (e.g. Grokbot QA)…" />
              )}
            />
          </Grid>
          {filterTesterUserId && (
            <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Verified on</InputLabel>
                <Select
                  value={filterVerifierPlatform}
                  label="Verified on"
                  onChange={(e) => { setFilterVerifierPlatform(e.target.value); setPage(0); }}
                  data-testid="qb-verifier-platform"
                >
                  <MenuItem value="web">Web</MenuItem>
                  <MenuItem value="mobile">Mobile</MenuItem>
                  <MenuItem value="">Any platform</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <Autocomplete
              options={examOptions}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={examOptions.find((o) => o.name === filterMockExamName) ?? null}
              inputValue={examInputValue}
              onInputChange={(_, val) => {
                setExamInputValue(val);
              }}
              onChange={(_, selected) => {
                const name = selected ? selected.name : "";
                setFilterMockExamName(name);
                setExamInputValue(name);
                setPage(0);
              }}
              loading={examLoading}
              size="small"
              freeSolo
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Mock exam"
                  placeholder="Search by exam name…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {examLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Year</InputLabel>
              <Select
                value={filterYearId}
                label="Year"
                onChange={(e) => {
                  setFilterYearId(e.target.value);
                  setFilterTopicIds([]);
                  setPage(0);
                }}
              >
                <MenuItem value="">All years</MenuItem>
                {schoolYears.map((year) => (
                  <MenuItem key={year.id} value={String(year.id)}>
                    {year.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Video</InputLabel>
              <Select
                value={filterHasVideo}
                label="Video"
                onChange={(e) => { setFilterHasVideo(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Video available</MenuItem>
                <MenuItem value="no">No video</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Sample</InputLabel>
              <Select
                value={filterSample}
                label="Sample"
                onChange={(e) => { setFilterSample(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Sample only</MenuItem>
                <MenuItem value="no">Not sample</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Convert-to-MCQ flag</InputLabel>
              <Select
                value={filterMcqRecommended}
                label="Convert-to-MCQ flag"
                onChange={(e) => { setFilterMcqRecommended(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Flagged for MCQ conversion</MenuItem>
                <MenuItem value="no">Not flagged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <TopicTreeSelect
              label="Topics"
              value={filterTopicIds}
              onChange={(ids) => {
                setFilterTopicIds(ids.map((id) => Number(id)));
                setPage(0);
              }}
              catalog={topicCatalog}
              yearId={filterYearId}
              onYearChange={(nextYearId) => {
                setFilterYearId(nextYearId);
                setFilterTopicIds([]);
                setPage(0);
              }}
              schoolYears={schoolYears}
              disabled={!filterSubjectId || topicCatalog.length === 0}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <TextField
              fullWidth
              size="small"
              label="Question ID"
              type="number"
              placeholder="e.g. 13124"
              value={filterQuestionId}
              onChange={(e) => { setFilterQuestionId(e.target.value); setPage(0); }}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Import Issues</InputLabel>
              <Select
                value={filterHasImportIssues}
                label="Import Issues"
                onChange={(e) => { setFilterHasImportIssues(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="yes">Has unresolved import issues</MenuItem>
                <MenuItem value="no">No unresolved import issues</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Issue Type</InputLabel>
              <Select
                value={filterImportIssueType}
                label="Issue Type"
                onChange={(e) => { setFilterImportIssueType(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All issue types</MenuItem>
                <MenuItem value="image_under_assignment">Image missing</MenuItem>
                <MenuItem value="image_assignment_low_coverage">Image coverage</MenuItem>
                <MenuItem value="text_fidelity_mismatch">Text mismatch</MenuItem>
                <MenuItem value="question_only_in_datalab">Missing imported question</MenuItem>
                <MenuItem value="answer_correlation_mismatch">Answer mismatch</MenuItem>
                <MenuItem value="orphaned_answer">Orphaned answer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <Autocomplete
              options={importOptions}
              getOptionLabel={(o) => (typeof o === "string" ? o : o._label || o.name || `#${o.id}`)}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={importOptions.find((o) => String(o.id) === filterPaperId) ?? null}
              inputValue={importInputValue}
              onInputChange={(_, val) => setImportInputValue(val)}
              onChange={(_, selected) => {
                setFilterPaperId(selected ? String(selected.id) : "");
                setImportInputValue(selected ? selected._label : "");
                setPage(0);
              }}
              loading={importLoading}
              size="small"
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Past paper"
                  placeholder="Search by paper name…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {importLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} sx={{ display: showMoreFilters ? undefined : 'none' }}>
            <Autocomplete
              options={worksheetOptions}
              getOptionLabel={(o) => (typeof o === "string" ? o : o.name ?? "")}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={worksheetOptions.find((o) => String(o.id) === filterWorksheetId) ?? null}
              inputValue={worksheetInputValue}
              onInputChange={(_, val) => setWorksheetInputValue(val)}
              onChange={(_, selected) => {
                setFilterWorksheetId(selected ? String(selected.id) : "");
                setWorksheetInputValue(selected ? selected.name : "");
                setPage(0);
              }}
              loading={worksheetLoading}
              size="small"
              clearOnEscape
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Worksheet"
                  placeholder="Search by worksheet name…"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {worksheetLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Grid>
        </Grid>
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
          <Button
            size="small"
            startIcon={<FilterList />}
            onClick={() => setShowMoreFilters((v) => !v)}
            aria-expanded={showMoreFilters}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {showMoreFilters ? "Fewer filters" : "More filters"}
          </Button>
        </Box>
      </Paper>

      {filterTesterUserId && verifierSummary && (
        <Alert severity="info" icon={false} sx={{ mb: 2 }} data-testid="qb-verifier-summary">
          <strong>{Number(verifierSummary.matching_questions ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</strong>
          {` question${verifierSummary.matching_questions === 1 ? "" : "s"} verified by `}
          <strong>{verifierSummary.user?.name}</strong>
          {verifierSummary.platform ? ` on ${verifierSummary.platform}` : " on any platform"}
          {activeFilterCount > 1 ? " match the current filters" : ""}
          {` (grouped questions count once; ${Number(verifierSummary.all?.questions ?? 0).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} question and part verifications by them in total`}
          {verifierSummary.all?.rejected > 0 ? `, ${Number(verifierSummary.all.rejected).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} rejected` : ""}
          {")."}
        </Alert>
      )}

      {/* Table */}
      <Paper sx={{ width: "100%" }}>
        <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 80 }}><strong>ID</strong></TableCell>
                <TableCell sx={{ width: 56 }}><strong>Q#</strong></TableCell>
                <TableCell sx={{ width: 180, maxWidth: 180 }}><strong>Question Text</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Category</strong></TableCell>
                <TableCell><strong>Subject</strong></TableCell>
                <TableCell><strong>Topics</strong></TableCell>
                <TableCell><strong>Source</strong></TableCell>
                <TableCell><strong>Difficulty</strong></TableCell>
                <TableCell><strong>Marks</strong></TableCell>
                <TableCell sx={{ minWidth: 80 }}><strong>Verify</strong></TableCell>
                <TableCell sx={{ minWidth: 88, maxWidth: 120, verticalAlign: "middle" }}><strong>Linked</strong></TableCell>
                <TableCell sx={{ minWidth: 100 }}><strong>Sample</strong></TableCell>
                <TableCell><strong>Video</strong></TableCell>
                <TableCell align="right" sx={stickyActionsHeaderSx}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? [...Array(5)].map((_, i) => (
                    <TableRow key={i}>
                      {[...Array(15)].map((_, j) => (
                        <TableCell key={j} sx={j === 14 ? stickyActionsBodySx : undefined}>
                          <Skeleton variant="text" width={80} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : questions.map((q) => (
                    <TableRow key={q.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 700,
                            color: "#7b1fa2",
                            bgcolor: "#f3e5f5",
                            borderRadius: 1,
                            px: 0.75,
                            py: 0.25,
                            display: "inline-block",
                            fontSize: "0.8rem",
                          }}
                        >
                          #{q.id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {q.paper_number != null ? (
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              color: "#0369a1",
                              bgcolor: "#e0f2fe",
                              borderRadius: 1,
                              px: 0.75,
                              py: 0.25,
                              display: "inline-block",
                              fontSize: "0.8rem",
                            }}
                          >
                            {q.paper_number}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 180, maxWidth: 180, verticalAlign: "middle", overflow: "hidden" }}>
                        <Box
                          sx={{
                            maxWidth: 180,
                            maxHeight: 24,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            fontSize: "0.8125rem",
                            lineHeight: "22px",
                            color: "text.primary",
                          }}
                          title={stripHtml(q.question_text)}
                        >
                          {stripHtml(q.question_text)}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, alignItems: "flex-start" }}>
                          <Chip
                            label={getTypeLabel(q.type)}
                            size="small"
                            sx={{
                              backgroundColor: typeColors[q.type] + "20",
                              color: typeColors[q.type],
                              fontWeight: 600,
                              fontSize: "0.7rem",
                            }}
                          />
                          {q.type === "grouped" && q.sub_questions_count > 0 && (
                            <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.68rem" }}>
                              {q.sub_questions_count} sub-question{q.sub_questions_count !== 1 ? "s" : ""}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>{q.category_name}</TableCell>
                      <TableCell sx={{ minWidth: 120 }}>{renderSubjectCell(q.subject)}</TableCell>
                      <TableCell sx={{ minWidth: 140, maxWidth: 220 }}>
                        {Array.isArray(q.topics) && q.topics.length > 0 ? (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.4 }}>
                            {q.topics.slice(0, 3).map((t) => (
                              <Chip
                                key={t.id}
                                label={t.name}
                                size="small"
                                variant="outlined"
                                sx={topicChipSx(t)}
                              />
                            ))}
                            {q.topics.length > 3 && (
                              <Chip
                                label={`+${q.topics.length - 3}`}
                                size="small"
                                sx={{ fontSize: "0.65rem", height: 20 }}
                              />
                            )}
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                          <Chip
                            label={sourceTypeLabel(q.source_type)}
                            size="small"
                            color={sourceTypeChipColor(q.source_type)}
                            variant="outlined"
                            sx={{ fontSize: "0.7rem", maxWidth: 160 }}
                          />
                          {q.mcq_conversion_job_id && (
                            <Chip
                              label="MCQ generated"
                              size="small"
                              color="secondary"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                          {q.mcq_recommended && (
                            <Chip
                              label="Convert to MCQ"
                              size="small"
                              color="warning"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell><DifficultyChip difficulty={q.difficulty} /></TableCell>
                      <TableCell>{q.default_marks}</TableCell>
                      <TableCell sx={{ minWidth: 80 }}>
                        <Box
                          onClick={(e) => handleVerifyChipClick(e, q.id)}
                          sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                        >
                          <AggregateChips aggregate={verificationSummary[`question:${q.id}`]} />
                        </Box>
                      </TableCell>
                      <TableCell sx={{ minWidth: 88, maxWidth: 120, verticalAlign: "middle" }}>
                        {renderLinkedResourceTags(q)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          {q.is_sample_question ? (
                            <Chip label="Sample" size="small" color="secondary" variant="filled" sx={{ fontSize: "0.65rem", height: 22 }} />
                          ) : (
                            <Typography variant="caption" color="text.secondary">—</Typography>
                          )}
                          {canUpdateQuestionBank ? (
                            <Tooltip title={q.is_sample_question ? "Remove sample flag" : "Mark as sample question"}>
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleToggleSample(q)}
                                  disabled={togglingSampleId === q.id}
                                  sx={{ color: q.is_sample_question ? "#7b1fa2" : "text.secondary" }}
                                >
                                  {togglingSampleId === q.id ? (
                                    <CircularProgress size={16} />
                                  ) : q.is_sample_question ? (
                                    <Bookmark fontSize="small" />
                                  ) : (
                                    <BookmarkBorder fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {q.video_generation_status === 'completed' && q.explanation_video_url ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Play video">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenVideo(q.explanation_video_url)}
                                sx={{ color: '#16a34a', p: 0.25 }}
                              >
                                <VideoLibrary sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Regenerate video">
                              <IconButton
                                size="small"
                                onClick={() => handleGenerateVideo(q)}
                                disabled={generatingVideoId === q.id}
                                sx={{ color: "#6366f1" }}
                              >
                                {generatingVideoId === q.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <RefreshIcon fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Remove video">
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveVideo(q)}
                                disabled={removingVideoId === q.id}
                                sx={{ color: "#dc2626" }}
                              >
                                {removingVideoId === q.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Delete fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : q.video_generation_status && ['pending', 'parsing', 'rendering', 'composing', 'uploading'].includes(q.video_generation_status) ? (
                          <Tooltip title={`Video ${q.video_generation_status}...`}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CircularProgress size={14} sx={{ color: '#6366f1' }} />
                              <Typography variant="caption" sx={{ color: '#6366f1', fontSize: '0.7rem' }}>
                                {q.video_generation_status}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : q.video_generation_status === 'failed' ? (
                          <Tooltip title="Video generation failed - Click to retry">
                            <IconButton
                              size="small"
                              onClick={() => handleGenerateVideo(q)}
                              disabled={generatingVideoId === q.id}
                              sx={{ color: "#dc2626" }}
                            >
                              {generatingVideoId === q.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PlayArrow fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Generate video">
                            <IconButton
                              size="small"
                              onClick={() => handleGenerateVideo(q)}
                              disabled={generatingVideoId === q.id}
                              sx={{ color: "#6366f1" }}
                            >
                              {generatingVideoId === q.id ? (
                                <CircularProgress size={16} />
                              ) : (
                                <PlayArrow fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={stickyActionsBodySx}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleView(q)}
                            sx={{ color: "#3f2b96" }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => navigate(`/admin/question-bank/${q.id}/edit`)}
                          >
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setDeletingQuestion(q);
                              setDeleteDialog(true);
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              {!loading && questions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={15} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No questions found.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: { xs: 1, sm: 2 },
            py: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              flexWrap: "wrap",
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 15, 25, 50, 100]}
              showFirstButton
              showLastButton
              labelRowsPerPage="Rows per page"
              labelDisplayedRows={({ from, to, count }) =>
                `Showing ${count === 0 ? 0 : from}-${count === 0 ? 0 : to} of ${
                  count === -1 ? `more than ${to}` : count
                } questions`
              }
              sx={{
                flexGrow: 1,
                ".MuiTablePagination-toolbar": {
                  minHeight: 44,
                  px: 0,
                  flexWrap: "wrap",
                  gap: 0.5,
                },
                ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows": {
                  m: 0,
                },
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Jump to page
              </Typography>
              <TextField
                size="small"
                type="number"
                value={jumpPageInput}
                onChange={(e) => setJumpPageInput(e.target.value)}
                onBlur={handleJumpToPage}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleJumpToPage();
                  }
                }}
                disabled={loading || total === 0}
                inputProps={{ min: 1, max: totalPages, style: { width: 56 } }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleJumpToPage}
                disabled={loading || total === 0}
              >
                Go
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {/* Delete Confirmation */}
      <Dialog
        open={deleteDialog}
        onClose={() => setDeleteDialog(false)}
        keepMounted={false}
        aria-modal="true"
      >
        <DialogTitle>Delete Question</DialogTitle>
        <DialogContent>
          {deletingQuestion?.linked_exams_count > 0 ? (
            <Typography color="error">
              This question is linked to {deletingQuestion.linked_exams_count} exam(s) and cannot be
              deleted. Unlink it first.
            </Typography>
          ) : (
            <Typography>
              Are you sure you want to delete this question? This action cannot be undone.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deletingQuestion?.linked_exams_count > 0}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Question Dialog */}
      {renderViewDialog()}

      {/* Verify tester details popover */}
      <Popover
        open={Boolean(verifyPopoverAnchor)}
        anchorEl={verifyPopoverAnchor}
        onClose={() => { setVerifyPopoverAnchor(null); setVerifyPopoverQId(null); setVerifyPopoverData([]); }}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ sx: { p: 2, minWidth: 280, maxWidth: 400 } }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>
          Verification details{verifyPopoverQId ? ` — Q#${verifyPopoverQId}` : ""}
        </Typography>
        {verifyPopoverLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : verifyPopoverData.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
            Not yet verified.
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {verifyPopoverData.map((v) => {
              const cfg = v.status === "approved"
                ? { label: "Approved", color: "success" }
                : v.status === "rejected"
                  ? { label: "Rejected", color: "error" }
                  : { label: v.status, color: "default" };
              return (
                <Box key={v.id} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1, fontWeight: 600 }}>
                    {v.user?.name ?? `User #${v.user_id}`}
                  </Typography>
                  <Chip label={cfg.label} color={cfg.color} size="small" variant="outlined" />
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {v.verified_at ? new Date(v.verified_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Popover>

      <VideoPlayerDialog
        open={videoPlayerOpen}
        onClose={handleCloseVideo}
        videoUrl={videoPlayerUrl}
        title="Explanation Video"
        allowDownload
      />
    </Box>
  );
};

export default QuestionBankList;
